import { z } from 'zod';
import { ThemePageTypeEnum } from './theme.schema';
import { StyleTokensGrammarSchema } from './element-props.schema';
import { ElementIdSchema, ElementTreeSchema, FieldPathSchema } from './element.schema';
import { editorEnvelope as envelope } from './theme-editor-channel.schema';
import { CustomCssSchema } from './custom-css.schema';
import {
  EditorDragCancelSchema, EditorDraggingSchema, EditorDragStartSchema, EditorDroppedSchema, EditorPointerSchema,
  EditorReleaseSchema
} from './theme-editor-drag.schema';

// The contract the theme builder and the storefront it previews talk over. A `postMessage` payload
// is untrusted input arriving from another window, so every message is parsed against a schema here
// before either side acts on it — the same rule the HTTP edge follows.

/** Sent once the storefront has a page on screen: which theme, and which page it drew. */
export const EditorReadySchema = z.object({
  ...envelope('storefront', 'ready'),
  slug: z.string(),
  page: ThemePageTypeEnum
}).strict();

/** Sent when a merchant clicks in the preview itself: the innermost node the click landed on. */
export const EditorClickedSchema = z.object({
  ...envelope('storefront', 'clicked'),
  elementId: ElementIdSchema.nullable(),
  /**
   * The run of style properties the rail should open on, named by a canvas chip that asks for one —
   * "Change background" is a request to open this node's background, not a different selection. It is a
   * name rather than a list of properties: the groups live in the builder's own Styles tab, which
   * resolves this against them and ignores a name it does not draw.
   */
  styleGroup: z.string().max(40).optional()
}).strict();

/**
 * Sent when a merchant edits text in the canvas itself rather than in the rail. The builder owns the
 * draft, so the preview reports the new value and waits to be told what to draw, exactly as it does
 * for a keystroke in the rail — the canvas never becomes a second source of truth.
 */
export const EditorTypedSchema = z.object({
  ...envelope('storefront', 'typed'),
  elementId: ElementIdSchema,
  propPath: FieldPathSchema,
  value: z.string().max(5000)
}).strict();

/**
 * Sent on every change: the draft, which the preview renders instead of what the store has saved. A tree
 * travels whole rather than as a patch — the builder owns the draft, and a tree the server has never seen
 * cannot be described as a difference from anything the preview already holds. Each part is optional, so
 * the code editor can send the one file it is editing: a page, the palette, or the stylesheet.
 */
export const EditorApplySchema = z.object({
  ...envelope('builder', 'apply'),
  tree: ElementTreeSchema.optional(),
  /** The store's own design tokens, laid over the theme's, so changing "Primary" repaints the page at once. */
  styles: StyleTokensGrammarSchema.optional(),
  /** The store's own stylesheet as it is being typed; an empty one draws the page without it. */
  customCss: CustomCssSchema.optional(),
  /** What the builder calls the nodes whose own label is not the name it shows — a section by its block, a placed
      block by its kind — so the canvas's toolbar and tag name them as Layers does. */
  labels: z.record(ElementIdSchema, z.string().trim().min(1).max(80))
    .refine((labels) => Object.keys(labels).length <= 500, 'At most 500 names').optional()
}).strict();

/** Sent when the tree's selection changes: outline this node, and scroll it into view when asked. */
export const EditorSelectSchema = z.object({
  ...envelope('builder', 'select'),
  elementId: ElementIdSchema.nullable(),
  reveal: z.boolean()
}).strict();

/** Sent when the pointer reaches a Layers row: outline that node on the page too, or nothing with null. */
export const EditorHoverSchema = z.object({
  ...envelope('builder', 'hover'),
  elementId: ElementIdSchema.nullable()
}).strict();

/** Sent when the pointer reaches a node on the page: light its Layers row too, or nothing with null. */
export const EditorHoveredSchema = z.object({
  ...envelope('storefront', 'hovered'),
  elementId: ElementIdSchema.nullable()
}).strict();

/**
 * Sent when a merchant uses the toolbar the preview floats over their selection. It is a request,
 * not a change: the builder owns the draft, so the canvas asks and waits to be told what to draw.
 */
export const EditorIntentSchema = z.object({
  ...envelope('storefront', 'intent'),
  intent: z.enum(['edit', 'moveUp', 'moveDown', 'duplicate', 'hide', 'delete']),
  elementId: ElementIdSchema
}).strict();

/**
 * Sent when a merchant presses a builder shortcut while the focus is inside the preview. The frame is a
 * different origin, so the builder's own key listener never sees those presses at all — which left undo
 * dead from the moment a merchant clicked the page, the first thing anyone does. The canvas forwards the
 * press rather than acting on it: the draft is the builder's, so this asks, exactly as an intent does.
 */
export const EditorShortcutSchema = z.object({
  ...envelope('storefront', 'shortcut'),
  action: z.enum(['undo', 'redo', 'save'])
}).strict();

/**
 * Sent when a merchant clicks the `+` the preview floats under the thing they are pointing at: open the
 * library, and put whatever they choose at this position. It is an insertion point, not a node, which is
 * why it cannot be an intent — every intent names a node that already exists.
 */
export const EditorInsertSchema = z.object({
  ...envelope('storefront', 'insert'),
  index: z.number().int().min(0).max(200),
  /** The container to insert inside; null is the page's own roots. */
  parentId: ElementIdSchema.nullable()
}).strict();

/**
 * Sent when a merchant changes how the page is being looked at rather than what is on it. `preview` puts
 * the builder's own chrome away so the page reads as a shopper meets it, and `xray` outlines every box at
 * once so the structure can be seen without pointing at each one in turn. Neither touches the draft, which
 * is why they travel together as one view rather than as two intents.
 */
export const EditorViewSchema = z.object({
  ...envelope('builder', 'view'),
  preview: z.boolean(),
  xray: z.boolean(),
  /** How far the builder shrinks the page to fit its canvas, so the page draws its editing chrome back at true size. */
  scale: z.number().positive().max(1).optional()
}).strict();

/** Everything the storefront may say to the builder. */
export const EditorToBuilderSchema = z.discriminatedUnion('type', [
  EditorReadySchema,
  EditorClickedSchema,
  EditorTypedSchema,
  EditorIntentSchema,
  EditorDroppedSchema,
  EditorInsertSchema,
  EditorShortcutSchema,
  EditorHoveredSchema,
  EditorDragStartSchema,
  EditorDragCancelSchema
]);

/** Everything the builder may say to the storefront. */
export const BuilderToEditorSchema = z.discriminatedUnion('type', [
  EditorApplySchema,
  EditorSelectSchema,
  EditorDraggingSchema,
  EditorViewSchema,
  EditorPointerSchema,
  EditorReleaseSchema,
  EditorHoverSchema
]);

export type EditorReadyDto = z.infer<typeof EditorReadySchema>;
export type EditorClickedDto = z.infer<typeof EditorClickedSchema>;
export type EditorTypedDto = z.infer<typeof EditorTypedSchema>;
export type EditorIntentDto = z.infer<typeof EditorIntentSchema>;
export type EditorDraggingDto = z.infer<typeof EditorDraggingSchema>;
export type EditorDroppedDto = z.infer<typeof EditorDroppedSchema>;
export type EditorPointerDto = z.infer<typeof EditorPointerSchema>;
export type EditorReleaseDto = z.infer<typeof EditorReleaseSchema>;
export type EditorInsertDto = z.infer<typeof EditorInsertSchema>;
export type EditorShortcutDto = z.infer<typeof EditorShortcutSchema>;
export type EditorApplyDto = z.infer<typeof EditorApplySchema>;
export type EditorSelectDto = z.infer<typeof EditorSelectSchema>;
export type EditorViewDto = z.infer<typeof EditorViewSchema>;
export type EditorHoverDto = z.infer<typeof EditorHoverSchema>;
export type EditorHoveredDto = z.infer<typeof EditorHoveredSchema>;
export type EditorDragStartDto = z.infer<typeof EditorDragStartSchema>;
export type EditorToBuilderDto = z.infer<typeof EditorToBuilderSchema>;
export type BuilderToEditorDto = z.infer<typeof BuilderToEditorSchema>;

// Reads a message the storefront sent the builder, or null when the payload is anything else.
export const readEditorMessage = (data: unknown): EditorToBuilderDto | null => {
  const parsed = EditorToBuilderSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
};

// Reads a message the builder sent the storefront, or null when the payload is anything else.
export const readBuilderMessage = (data: unknown): BuilderToEditorDto | null => {
  const parsed = BuilderToEditorSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
};
